// hlcClock.js — stavová vrstva nad HLC (crdtMerge.hlcNow/hlcCompare). Razí monotónny
// logický čas pre sync merge a drží last-seen HLC.
//
// PREČO logické hodiny a nie wall-clock (updated_at): clock skew telefón↔desktop dáva ~12 %
// zlých LWW merge (roadmapa #3). HLC = { ts, count, node } je monotónne rastúci naprieč
// zariadeniami: aj keď dvom zariadeniam nesedia hodiny, `count` a tie-break podľa `node`
// zaručia deterministické totálne usporiadanie.
//
// Stav (last HLC) žije v localStorage per-device — NIKDY v settings/DB, lebo tie sa
// synchronizujú a lokálny clock sa nesmie prepísať cudzím stavom (rovnaký princíp ako
// getSyncDeviceId v driveSync.js). deviceId (`node`) dodáva volajúci cez setHlcNode().
import { hlcNow, hlcCompare } from './crdtMerge';

export const HLC_STORAGE_KEY = 'todue_sync_hlc_v1';

// Max tolerovaný predstih remote HLC oproti lokálnemu wall-clocku pri POSUNE lokálnych hodín
// (observeHlc). Reálny honest skew (NTP jitter, dočasne offline telefón) je sekundy–minúty;
// hodina je štedrá horná hranica, ktorú poctivé spotrebiteľské zariadenie nedosiahne (UTC
// Date.now() nezávisí od timezone). Zariadenie s mŕtvou CMOS baterkou / zle nastaveným dátumom
// razí ts roky dopredu — bez tohto stropu observeHlc adoptuje ten čas do lokálnych hodín →
// clock je OTRÁVENÝ natrvalo a šíri sa cez sync na CELÚ flotilu. Empiricky reprodukované
// (hlcClock.robust.test.js: honest device → 2096); cross-family potvrdené (Gemini 2026-07-26).
export const MAX_CLOCK_DRIFT_MS = 60 * 60 * 1000; // 1 h

let _node = null;
let _lastHlc = null;
let _loaded = false;

function defaultStorage() {
  try { return globalThis.localStorage || null; } catch { return null; }
}

/** Nastav identitu zariadenia (`node`) — 8-hex z getSyncDeviceId(). Povinné pred nextHlc(). */
export function setHlcNode(node) {
  if (!node || typeof node !== 'string') throw new Error('[hlcClock] node musí byť neprázdny string');
  _node = node;
}

/** Serializuj HLC na stabilný string (persistencia / Drive payload). */
export function serializeHlc(hlc) {
  if (!hlc) return null;
  return `${hlc.ts}:${hlc.count}:${hlc.node}`;
}

/** Parsuj HLC zo stringu; nevalidný tvar → null (nikdy nefabrikuj čiastočný HLC). */
export function parseHlc(str) {
  if (typeof str !== 'string') return null;
  const m = /^(\d+):(\d+):(.+)$/.exec(str);
  if (!m) return null;
  const ts = Number(m[1]);
  const count = Number(m[2]);
  if (!Number.isInteger(ts) || !Number.isInteger(count) || ts < 0 || count < 0) return null;
  return { ts, count, node: m[3] };
}

/** Načítaj posledný HLC z úložiska do pamäte (idempotentné; volá sa lazy pred prvým razením). */
export function loadHlc(storage = defaultStorage()) {
  if (_loaded) return _lastHlc;
  try {
    const raw = storage?.getItem?.(HLC_STORAGE_KEY);
    _lastHlc = parseHlc(raw);
  } catch { _lastHlc = null; }
  _loaded = true;
  return _lastHlc;
}

/**
 * Read-before-mint reconcile (Sol cross-family nález 2026-07-21): pred KAŽDÝM razením zosúlaď
 * in-memory clock s tým, čo je práve v úložisku, a drž VYŠŠÍ z oboch. Rieši:
 *  - multi-tab race (#1): druhý tab toho istého browsera zdieľa localStorage, ale má vlastný
 *    in-memory `_lastHlc` → bez re-čítania by vyrazil duplikát. Teraz dobehne za cudzí tab.
 *  - persistence failure (#3): keď getItem() hodí (Safari private / quota), NIKDY neprepíš
 *    novší in-memory stav null-om → clock sa nesmie vrátiť späť. Držíme max(in-memory, persisted).
 * Nikdy nejde späť: berie sa len striktne vyšší HLC.
 */
function reconcile(storage) {
  let persisted = null;
  try { persisted = parseHlc(storage?.getItem?.(HLC_STORAGE_KEY)); } catch { persisted = null; }
  if (persisted && (!_lastHlc || hlcCompare(persisted, _lastHlc) > 0)) _lastHlc = persisted;
  _loaded = true;
  return _lastHlc;
}

/** Wall-clock → bezpečné nezáporné celé číslo (Sol nález #4: zlomkový/záporný wallMs sa
 *  serializuje na tvar, ktorý parseHlc odmietne → po reštarte backward). Date.now() je vždy
 *  integer; guard chráni injektovanú cestu aj exotické prostredia. */
function safeWall(wallMs) {
  const raw = Number.isFinite(wallMs) ? wallMs : Date.now();
  const n = Math.floor(raw);
  return n > 0 ? n : 0;
}

function persist(hlc, storage) {
  try { storage?.setItem?.(HLC_STORAGE_KEY, serializeHlc(hlc)); } catch { /* LS nedostupné → in-memory only */ }
}

/** Posledný vyrazený/videný HLC (bez posunu). Null ak sa ešte nič nerazilo. */
export function currentHlc(storage = defaultStorage()) {
  if (!_loaded) loadHlc(storage);
  return _lastHlc;
}

/**
 * Vyrazí NOVÝ HLC pri lokálnej udalosti (zápis). Monotónne rastúci oproti poslednému —
 * ak wall-clock nešiel dopredu (stagnácia/skok späť), inkrementuje `count`. Persistuje.
 * @param {number} [wallMs] injektovateľný wall-clock (default Date.now()) — pre testy/determinizmus.
 */
export function nextHlc(wallMs, storage = defaultStorage()) {
  if (!_node) throw new Error('[hlcClock] setHlcNode() musí prebehnúť pred nextHlc()');
  reconcile(storage); // read-before-mint: dobehni za cudzí tab / neklesni pri zlyhaní úložiska
  const next = hlcNow(_lastHlc, _node, safeWall(wallMs));
  _lastHlc = next;
  persist(next, storage);
  return next;
}

/**
 * Prijmi remote HLC a posuň lokálny clock ZA neho (HLC receive). Zaručí, že najbližší
 * nextHlc() bude > remote aj > local — kauzalita naprieč zariadeniami. Persistuje posun.
 * Vráti aktualizovaný lokálny HLC.
 *
 * Clock-skew guard (K4 sync-resilience, 2026-07-26): predstih remote nad lokálnym wall-clockom
 * väčší než MAX_CLOCK_DRIFT_MS sa NEadoptuje do lokálnych hodín (bounded advance) — inak jedno
 * zariadenie so zlým dátumom otrávi hodiny celej flotily na roky dopredu. VEDOMÉ OBMEDZENIE:
 * samotný remote ZÁZNAM si drží surový HLC (jeho per-record merge sa NEMENÍ), preto capujeme LEN
 * posun lokálnych hodín, nie uložený stamp — clamp uloženého HLC na `wall+DRIFT` by bol
 * NEKOMUTATÍVNY (wall sa líši per-device/čas → dve zariadenia by uložili iný HLC → divergencia,
 * horší bug). Reziduum: edit na TOM ISTOM poli poškodeného záznamu stále prehráva jeho budúcemu
 * stampu, ale prehratá hodnota ide do sync_conflicts (audit, NIE tichá strata) a nová otrava
 * hodín sa už nešíri. Cap sa aktivuje len pri REÁLNOM epoch wall-clocku (`wall > DRIFT`), aby
 * degenerovaný/testovací malý wall (abstraktné HLC) legitímny remote neodmietol.
 * @param {{ts:number,count:number,node:string}} remote
 */
export function observeHlc(remote, wallMs, storage = defaultStorage()) {
  if (!remote) return currentHlc(storage);
  reconcile(storage); // rovnaký read-before-mint pred posunom (multi-tab / persistence guard)
  const wall = safeWall(wallMs);
  // Bounded advance: ak remote fyzický čas absurdne predbieha náš reálny clock, adoptuj len po
  // wall+DRIFT. `count`/`node` remote ostávajú (neovplyvnia kauzalitu), capuje sa len `ts`.
  const capActive = wall > MAX_CLOCK_DRIFT_MS
    && Number.isFinite(remote.ts) && remote.ts > wall + MAX_CLOCK_DRIFT_MS;
  const guarded = capActive
    ? { ts: wall + MAX_CLOCK_DRIFT_MS, count: remote.count, node: remote.node }
    : remote;
  // lokálny základ = vyšší z (doterajší lokálny, guarded remote) — recv musí dobehnúť za cudzí čas
  const base = _lastHlc && hlcCompare(_lastHlc, guarded) >= 0 ? _lastHlc : guarded;
  // hlcNow(base, node, now): ak now > base.ts → {now,0}; inak {base.ts, base.count+1} — vždy > base
  const next = hlcNow(base, _node || base.node, wall);
  _lastHlc = next;
  persist(next, storage);
  return next;
}

// KNOWN-LIMIT (Sol nález #2, akceptované): pri stagnujúcom wall-clock `count` rastie bez stropu;
// pretečenie za Number.MAX_SAFE_INTEGER je prakticky nedosiahnuteľné (count sa nuluje pri každom
// posune ms → potreboval by ~9e15 zápisov v jednej milisekunde). Ak by sa sync raz škáloval na
// dávkové stampovanie v rámci 1 ms, tu treba strop + rollover na ts+1.

/** Test-only: vyčisti in-memory stav. `clearNode=true` vynuluje aj node (default node ostáva). */
export function _resetHlcForTest(clearNode = false) {
  _lastHlc = null;
  _loaded = false;
  if (clearNode) _node = null;
}
